import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Download, History } from 'lucide-react';
import { Layout } from '../components/Layout';
import { apiService } from '../services/api';
import type { BatchEvent } from '../services/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { useAuth } from '../hooks/useAuth';

const EVENT_TYPE_LABELS: Record<string, string> = {
  sent_to_bank: 'Sent to bank',
  fack_acknowledged: 'Bank acknowledged',
  fack_rejected: 'Bank rejected',
  ack_received: 'Acknowledgement received',
  tx_unmatched: 'Transaction unmatched',
  tx_rejected: 'Transaction rejected',
  tx_accepted: 'Transaction accepted',
  bank_transferred: 'Bank transfer confirmed',
  paid: 'Marked as paid in Fortnox',
  fortnox_failed: 'Fortnox update failed',
  cancelled: 'Batch cancelled',
  deleted: 'Batch deleted',
  manually_marked_paid: 'Manually marked as paid',
  fortnox_force_queued: 'Force Fortnox pay queued',
  fortnox_force_created: 'Force Fortnox pay completed',
  fortnox_force_failed: 'Force Fortnox pay failed',
};

interface Invoice {
  documentNumber: string;
  invoiceNumber: string;
  supplierNumber: string;
  supplierName: string;
  amount: number;
  currency: string;
  dueDate: string;
  invoiceDate: string;
  paymentMethod: string | null;
  thirdPartyEligible: boolean;
  issues: string[];
  itemStatus: string | null;
  batchId: number | null;
  batchReference: string | null;
  errorMessage: string | null;
}

interface BatchItem {
  documentNumber: string | null;
  supplierName: string;
  amount: number;
  currency: string;
  paymentType: string;
  status: string;
  errorMessage: string | null;
}

interface Batch {
  id: number;
  batchReference: string;
  status: string;
  batchType: string;
  createdAt: string;
  confirmedAt: string | null;
  executionDate: string | null;
  errorMessage: string | null;
  itemCount: number;
  totalAmount: number;
  parentBatchId: number | null;
  items: BatchItem[];
}

type Tab = 'invoices' | 'batches';
type SortKey = 'supplierName' | 'documentNumber' | 'amount' | 'dueDate' | 'paymentMethod' | 'status';
type SortDir = 'asc' | 'desc';

const BATCH_STATUS_OPTIONS: Record<'citi' | 'third_party', { value: string; label: string }[]> = {
  citi: [
    { value: 'pending', label: 'Pending' },
    { value: 'exported', label: 'Exported' },
    { value: 'acknowledged', label: 'Acknowledged' },
    { value: 'being_paid', label: 'Being paid' },
    { value: 'bank_approved', label: 'Bank Approved' },
    { value: 'bank_transferred', label: 'Bank Transferred' },
    { value: 'bank_failed', label: 'Bank Failed' },
    { value: 'fortnox_failed', label: 'Fortnox Failed' },
    { value: 'completed', label: 'Completed' },
    { value: 'partial', label: 'Partial' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'deleted', label: 'Deleted' },
  ],
  third_party: [
    { value: 'being_paid', label: 'Being paid' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'deleted', label: 'Deleted' },
  ],
};

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  pending:       { bg: 'bg-blue-50',   text: 'text-blue-600',   border: 'border-blue-100',   label: 'Pending' },
  exported:      { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-100', label: 'Exported' },
  being_paid:    { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-100', label: 'Being Paid' },
  bank_approved: { bg: 'bg-cyan-50',   text: 'text-cyan-600',   border: 'border-cyan-100',   label: 'Bank Approved' },
  bank_transferred: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-100', label: 'Bank Transferred' },
  bank_failed:   { bg: 'bg-red-50',    text: 'text-red-600',    border: 'border-red-100',    label: 'Bank Failed' },
  paid:          { bg: 'bg-emerald-50',text: 'text-emerald-600',border: 'border-emerald-100',label: 'Paid' },
  completed:     { bg: 'bg-emerald-50',text: 'text-emerald-600',border: 'border-emerald-100',label: 'Completed' },
  fortnox_failed:{ bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100', label: 'Fortnox Failed' },
  cancelled:     { bg: 'bg-muted',     text: 'text-muted-foreground', border: 'border-border', label: 'Cancelled' },
  deleted:       { bg: 'bg-muted',     text: 'text-muted-foreground', border: 'border-border', label: 'Deleted' },
};

function InvoiceStatusBadge({ status, issues, errorMessage }: { status: string | null; issues: string[]; errorMessage?: string | null }) {
  if (status) {
    const s = STATUS_STYLES[status] ?? { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', label: status };
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs ${s.bg} ${s.text} border ${s.border} px-2 py-0.5 rounded-full`}
        title={errorMessage ?? undefined}
      >
        {s.label}
        {errorMessage && <span className="opacity-70">ⓘ</span>}
      </span>
    );
  }
  if (issues.length > 0) {
    return (
      <span className="inline-flex items-center text-xs bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded-full" title={issues.join('\n')}>
        {issues.length} issue{issues.length > 1 ? 's' : ''}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-xs bg-emerald-50 text-emerald-600 border border-emerald-100 px-2 py-0.5 rounded-full">
      Ready
    </span>
  );
}

function ConfirmModal({
  message,
  confirmLabel = 'Delete',
  destructive = true,
  onConfirm,
  onCancel,
}: {
  message: string;
  confirmLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl border border-border/60 p-6 w-full max-w-sm mx-4 space-y-4">
        <p className="text-sm text-foreground">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-8 px-4 rounded-md border border-border/60 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`h-8 px-4 rounded-md text-sm font-medium hover:opacity-80 transition-opacity ${
              destructive ? 'bg-destructive text-white' : 'bg-foreground text-background'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function BatchTimelineModal({
  events,
  loading,
  onClose,
}: {
  events: BatchEvent[];
  loading: boolean;
  onClose: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl border border-border/60 p-6 w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold">Batch timeline</p>
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Close
          </button>
        </div>
        <div className="overflow-y-auto space-y-3">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events recorded yet.</p>
          ) : (
            events.map((event) => (
              <div key={event.id} className="border-l-2 border-border/60 pl-3 py-0.5">
                <p className="text-xs text-muted-foreground">{formatDateTime(event.createdAt)}</p>
                <p className="text-sm font-medium">
                  {EVENT_TYPE_LABELS[event.eventType] ?? event.eventType}
                  {event.supplierName && ` — ${event.supplierName}`}
                  {event.documentNumber && ` (#${event.documentNumber})`}
                </p>
                {event.reason && <p className="text-xs text-muted-foreground mt-0.5">{event.reason}</p>}
                {event.sourceFile && (
                  <p className="text-xs text-muted-foreground mt-0.5 font-mono break-all">{event.sourceFile}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function statusSortKey(inv: Invoice): string {
  if (inv.itemStatus) return inv.itemStatus;
  if (inv.issues.length > 0) return '_issues';
  return '_ready';
}

export function Payments() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('invoices');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('documentNumber');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [search, setSearch] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [dueDateFilter, setDueDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [executionDate, setExecutionDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [groupByDueDate, setGroupByDueDate] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingExportThirdParty, setLoadingExportThirdParty] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingMoreBatches, setLoadingMoreBatches] = useState(false);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchOffset, setBatchOffset] = useState(0);
  const [batchStatusFilter, setBatchStatusFilter] = useState('');
  const [batchTypeFilter, setBatchTypeFilter] = useState('');
  const [batchCurrencyFilter, setBatchCurrencyFilter] = useState('');
  const [batchCurrencies, setBatchCurrencies] = useState<string[]>([]);
  const [expandedBatches, setExpandedBatches] = useState<Set<number>>(new Set());
  const [confirmingBatch, setConfirmingBatch] = useState<number | null>(null);
  const [deletingBatch, setDeletingBatch] = useState<number | null>(null);
  const [pendingSendBatch, setPendingSendBatch] = useState<number | null>(null);
  const [pendingCancelBatch, setPendingCancelBatch] = useState<number | null>(null);
  const [cancellingBatch, setCancellingBatch] = useState<number | null>(null);
  const [pendingMarkPaidBatch, setPendingMarkPaidBatch] = useState<number | null>(null);
  const [markingPaidBatch, setMarkingPaidBatch] = useState<number | null>(null);
  const [pendingForceFortnoxBatch, setPendingForceFortnoxBatch] = useState<number | null>(null);
  const [forcingFortnoxBatch, setForcingFortnoxBatch] = useState<number | null>(null);
  const [resubmittingBatch, setResubmittingBatch] = useState<number | null>(null);
  const [downloadingBatch, setDownloadingBatch] = useState<number | null>(null);
  const [viewingTimelineBatch, setViewingTimelineBatch] = useState<number | null>(null);
  const [timelineEvents, setTimelineEvents] = useState<BatchEvent[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    setError('');
    try {
      const data = await apiService.getInvoices();
      setInvoices(data.invoices);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  const BATCH_PAGE_SIZE = 10;

  const loadBatches = useCallback(async (showLoading = true, status = '', batchType = '', currency = '') => {
    if (showLoading) setLoadingBatches(true);
    setBatchOffset(0);
    try {
      const data = await apiService.getBatches({ limit: BATCH_PAGE_SIZE, offset: 0, status: status || undefined, batchType: batchType || undefined, currency: currency || undefined });
      setBatches(data.batches);
      setBatchTotal(data.total);
    } catch {
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  const loadMoreBatches = useCallback(async () => {
    setLoadingMoreBatches(true);
    const nextOffset = batchOffset + BATCH_PAGE_SIZE;
    try {
      const data = await apiService.getBatches({ limit: BATCH_PAGE_SIZE, offset: nextOffset, status: batchStatusFilter || undefined, batchType: batchTypeFilter || undefined, currency: batchCurrencyFilter || undefined });
      setBatches((prev) => [...prev, ...data.batches]);
      setBatchOffset(nextOffset);
      setBatchTotal(data.total);
    } catch {
    } finally {
      setLoadingMoreBatches(false);
    }
  }, [batchOffset, batchStatusFilter, batchTypeFilter, batchCurrencyFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);
  useEffect(() => { if (tab === 'batches') loadBatches(true, batchStatusFilter, batchTypeFilter, batchCurrencyFilter); }, [tab, loadBatches, batchStatusFilter, batchTypeFilter, batchCurrencyFilter]);
  useEffect(() => { apiService.getBatchCurrencies().then((data) => setBatchCurrencies(data.currencies)).catch(() => {}); }, []);

  const toggleBatchExpanded = (batchId: number) => {
    setExpandedBatches((prev) => (prev.has(batchId) ? new Set() : new Set([batchId])));
  };

  useEffect(() => {
    if (viewingTimelineBatch === null) return;
    let cancelled = false;
    setLoadingTimeline(true);
    apiService.getBatchEvents(viewingTimelineBatch)
      .then((data) => { if (!cancelled) setTimelineEvents(data.events); })
      .catch(() => { if (!cancelled) setTimelineEvents([]); })
      .finally(() => { if (!cancelled) setLoadingTimeline(false); });
    return () => { cancelled = true; };
  }, [viewingTimelineBatch]);

  const toggleSelect = (docNum: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(docNum) ? next.delete(docNum) : next.add(docNum);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      pageAvailableInvoices.forEach((i) => (allPageSelected ? next.delete(i.documentNumber) : next.add(i.documentNumber)));
      return next;
    });
  };

  const handleExport = async () => {
    if (!selected.size) return;
    setLoadingExport(true);
    setError('');
    try {
      await apiService.exportPayments(Array.from(selected), executionDate, groupByDueDate);
      setTab('batches');
      setSelected(new Set());
      await loadBatches();
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setLoadingExport(false);
    }
  };

  const allSelectedThirdPartyEligible = selected.size > 0 && Array.from(selected).every(
    (doc) => invoices.find((i) => i.documentNumber === doc)?.thirdPartyEligible
  );

  const handleExportThirdParty = async () => {
    if (!selected.size || !allSelectedThirdPartyEligible) return;
    setLoadingExportThirdParty(true);
    setError('');
    try {
      await apiService.exportThirdParty(Array.from(selected), executionDate, groupByDueDate);
      setTab('batches');
      setSelected(new Set());
      await loadBatches();
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setLoadingExportThirdParty(false);
    }
  };

  const handleConfirm = async (batchId: number) => {
    setConfirmingBatch(batchId);
    setError('');
    try {
      await apiService.confirmBatch(batchId);
      await loadBatches();
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send batch to bank');
    } finally {
      setConfirmingBatch(null);
    }
  };

const handleResubmitFailed = async (batchId: number) => {
    setResubmittingBatch(batchId);
    setError('');
    try {
      await apiService.resubmitFailed(batchId);
      await loadBatches();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Resubmit failed');
    } finally {
      setResubmittingBatch(null);
    }
  };

  const handleCancelBatch = async (batchId: number) => {
    setPendingCancelBatch(null);
    setCancellingBatch(batchId);
    setError('');
    try {
      await apiService.cancelBatch(batchId);
      await loadBatches();
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel batch');
    } finally {
      setCancellingBatch(null);
    }
  };

  const handleMarkPaid = async (batchId: number) => {
    setPendingMarkPaidBatch(null);
    setMarkingPaidBatch(batchId);
    setError('');
    try {
      await apiService.markBatchPaid(batchId);
      await loadBatches(true, batchStatusFilter, batchTypeFilter, batchCurrencyFilter);
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark batch as paid');
    } finally {
      setMarkingPaidBatch(null);
    }
  };

  const handleForceFortnoxPay = async (batchId: number) => {
    setPendingForceFortnoxBatch(null);
    setForcingFortnoxBatch(batchId);
    setError('');
    setNotice('');
    try {
      const result = await apiService.forceFortnoxPay(batchId);
      setNotice(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to force Fortnox payment');
    } finally {
      setForcingFortnoxBatch(null);
    }
  };

  const handleDeleteBatch = async (batchId: number) => {
    setDeletingBatch(null);
    try {
      await apiService.deleteBatch(batchId);
      await loadBatches();
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete batch');
    }
  };

  const handleDownloadBatch = async (batchId: number) => {
    setDownloadingBatch(batchId);
    try {
      await apiService.downloadBatch(batchId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloadingBatch(null);
    }
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case 'supplierName':  cmp = a.supplierName.localeCompare(b.supplierName); break;
      case 'documentNumber': cmp = (a.documentNumber || a.invoiceNumber).localeCompare(b.documentNumber || b.invoiceNumber, undefined, { numeric: true }); break;
      case 'amount':        cmp = a.amount - b.amount; break;
      case 'dueDate':       cmp = a.dueDate.localeCompare(b.dueDate); break;
      case 'paymentMethod': cmp = (a.paymentMethod ?? '').localeCompare(b.paymentMethod ?? ''); break;
      case 'status':        cmp = statusSortKey(a).localeCompare(statusSortKey(b)); break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const currencies = Array.from(new Set(invoices.map((i) => i.currency))).sort();

  const filteredInvoices = sortedInvoices.filter((inv) => {
    if (currencyFilter && inv.currency !== currencyFilter) return false;
    if (dueDateFilter && inv.dueDate !== dueDateFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const statusLabel = inv.itemStatus
      ? (STATUS_STYLES[inv.itemStatus]?.label ?? inv.itemStatus)
      : inv.issues.length > 0 ? 'issues' : 'ready';
    return (
      inv.supplierName.toLowerCase().includes(q) ||
      (inv.documentNumber || inv.invoiceNumber).toLowerCase().includes(q) ||
      inv.amount.toString().includes(q) ||
      inv.dueDate.includes(q) ||
      (inv.paymentMethod ?? '').toLowerCase().includes(q) ||
      statusLabel.toLowerCase().includes(q)
    );
  });

  const selectedHasIssues = Array.from(selected).some(
    (doc) => invoices.find((i) => i.documentNumber === doc)?.issues.length
  );

  const selectedTotalsByCurrency = Array.from(selected).reduce((acc, doc) => {
    const inv = invoices.find((i) => i.documentNumber === doc);
    if (inv) acc[inv.currency] = (acc[inv.currency] ?? 0) + inv.amount;
    return acc;
  }, {} as Record<string, number>);

  const INVOICES_PAGE_SIZE = 50;
  const pageCount = Math.max(1, Math.ceil(filteredInvoices.length / INVOICES_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedInvoices = filteredInvoices.slice(
    (currentPage - 1) * INVOICES_PAGE_SIZE,
    currentPage * INVOICES_PAGE_SIZE
  );
  const pageAvailableInvoices = pagedInvoices.filter((i) => !i.itemStatus);
  const allPageSelected = pageAvailableInvoices.length > 0 && pageAvailableInvoices.every((i) => selected.has(i.documentNumber));

  useEffect(() => { setPage(1); }, [search, currencyFilter, dueDateFilter, sortKey, sortDir]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Payments</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage supplier invoice payments to Citi Bank.</p>
          </div>
          {selected.size > 0 && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Selected total ({selected.size})</p>
              <p className="text-lg font-semibold tracking-tight">
                {Object.entries(selectedTotalsByCurrency)
                  .map(([ccy, amt]) => `${amt.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} ${ccy}`)
                  .join(' · ')}
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border/60">
          {(['invoices', 'batches'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                tab === t
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-2.5">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {notice && (
          <div className="flex items-start justify-between gap-3 rounded-md bg-emerald-500/10 border border-emerald-500/20 px-4 py-2.5">
            <p className="text-sm text-emerald-700 dark:text-emerald-400">{notice}</p>
            <button onClick={() => setNotice('')} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
          </div>
        )}

        {/* Invoices Tab */}
        {tab === 'invoices' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Execution date</label>
                <input
                  type="date"
                  lang="sv"
                  value={executionDate}
                  min={new Date().toISOString().slice(0, 10)}
                  disabled={groupByDueDate}
                  onChange={(e) => {
                    const today = new Date().toISOString().slice(0, 10);
                    if (e.target.value >= today) setExecutionDate(e.target.value);
                  }}
                  className="text-sm border border-border/60 rounded-md px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20 disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </div>

              <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={groupByDueDate}
                  onChange={(e) => setGroupByDueDate(e.target.checked)}
                  className="rounded"
                />
                Set due date for execution
              </label>

              <button
                onClick={handleExport}
                disabled={!selected.size || loadingExport}
                className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-30"
              >
                {loadingExport ? 'Generating…' : `Export${selected.size ? ` (${selected.size})` : ''} to Citi XML`}
              </button>

              <button
                onClick={handleExportThirdParty}
                disabled={!selected.size || loadingExportThirdParty || !allSelectedThirdPartyEligible}
                className="h-9 px-4 rounded-md border border-border/60 bg-white text-sm font-medium transition-colors hover:bg-muted/40 disabled:opacity-30"
                title={selected.size && !allSelectedThirdPartyEligible ? 'Third Party export requires supplier IBAN+BIC or account number+clearing number' : undefined}
              >
                {loadingExportThirdParty ? 'Generating…' : `Export${selected.size ? ` (${selected.size})` : ''} to Third Party`}
              </button>

              {selectedHasIssues && (
                <span className="text-sm text-destructive">Fix issues before exporting</span>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 h-8 text-sm border border-border/60 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-foreground/20"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs leading-none"
                  >✕</button>
                )}
              </div>

              <select
                value={currencyFilter}
                onChange={(e) => { setCurrencyFilter(e.target.value); setSelected(new Set()); }}
                className="h-8 px-2.5 text-sm border border-border/60 rounded-md bg-white text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <option value="">All currencies</option>
                {currencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>

              <input
                type="date"
                lang="sv"
                value={dueDateFilter}
                onChange={(e) => setDueDateFilter(e.target.value)}
                className="h-8 text-sm border border-border/60 rounded-md px-2.5 bg-white text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
                title="Filter by due date"
              />
              {dueDateFilter && (
                <button
                  onClick={() => setDueDateFilter('')}
                  className="text-xs text-muted-foreground hover:text-foreground leading-none"
                >✕</button>
              )}

              {(search || currencyFilter || dueDateFilter) && (
                <span className="text-xs text-muted-foreground">
                  {filteredInvoices.length} of {invoices.length}
                </span>
              )}
              <button
                onClick={loadInvoices}
                disabled={loadingInvoices}
                className="h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              >
                {loadingInvoices ? 'Loading…' : 'Refresh'}
              </button>
              <button
                onClick={() => {
                  setSearch('');
                  setCurrencyFilter('');
                  setDueDateFilter('');
                  setSelected(new Set());
                }}
                disabled={!(search || currencyFilter || dueDateFilter || selected.size > 0)}
                className="h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              >
                Clear
              </button>
            </div>

            <div className="bg-[#fafafa] border border-border/60 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/60">
                    <th className="p-3 w-10 text-left">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    {([
                      { key: 'supplierName',   label: 'Supplier',   align: 'left'  },
                      { key: 'documentNumber', label: 'Invoice #',  align: 'left'  },
                      { key: 'amount',         label: 'Amount',     align: 'right' },
                      { key: 'dueDate',        label: 'Due Date',   align: 'left'  },
                      { key: 'paymentMethod',  label: 'Method',     align: 'left'  },
                      { key: 'status',         label: 'Status',     align: 'left'  },
                    ] as { key: SortKey; label: string; align: 'left' | 'right' }[]).map(({ key, label, align }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        className={`p-3 font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          <span className="text-[10px] opacity-50">
                            {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingInvoices ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">
                        Loading invoices…
                      </td>
                    </tr>
                  ) : filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">
                        {invoices.length === 0 ? 'No unpaid invoices found' : 'No invoices match your filters'}
                      </td>
                    </tr>
                  ) : (
                    pagedInvoices.map((inv) => (
                      <tr key={inv.documentNumber} className="border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors">
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selected.has(inv.documentNumber)}
                            disabled={!!inv.itemStatus}
                            onChange={() => toggleSelect(inv.documentNumber)}
                            className="rounded"
                          />
                        </td>
                        <td className="p-3 font-medium">{inv.supplierName}</td>
                        <td className="p-3 text-muted-foreground font-mono text-xs">{inv.documentNumber || inv.invoiceNumber}</td>
                        <td className="p-3 text-right font-mono text-xs">
                          {inv.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {inv.currency}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">{formatDate(inv.dueDate)}</td>
                        <td className="p-3 text-xs">{inv.paymentMethod ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-3">
                          <InvoiceStatusBadge status={inv.itemStatus} issues={inv.issues} errorMessage={inv.errorMessage} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredInvoices.length > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Showing {(currentPage - 1) * INVOICES_PAGE_SIZE + 1}–{Math.min(currentPage * INVOICES_PAGE_SIZE, filteredInvoices.length)} of {filteredInvoices.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="h-8 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-muted-foreground">
                    Page {currentPage} of {pageCount}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={currentPage >= pageCount}
                    className="h-8 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {Array.from(selected).some((doc) => invoices.find((i) => i.documentNumber === doc)?.issues.length) && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-amber-700">Issues in selection</p>
                {Array.from(selected)
                  .map((doc) => invoices.find((i) => i.documentNumber === doc))
                  .filter((inv) => inv && inv.issues.length > 0)
                  .map((inv) => (
                    <div key={inv!.documentNumber} className="text-xs">
                      <span className="font-medium text-amber-800">{inv!.supplierName}</span>
                      <ul className="ml-4 mt-1 list-disc text-amber-700 space-y-0.5">
                        {inv!.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                      </ul>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Batches Tab */}
        {tab === 'batches' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={batchStatusFilter}
                onChange={(e) => setBatchStatusFilter(e.target.value)}
                className="h-9 px-2.5 rounded-md border border-border/60 bg-white text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <option value="">All statuses</option>
                {(batchTypeFilter === 'citi' || batchTypeFilter === 'third_party'
                  ? BATCH_STATUS_OPTIONS[batchTypeFilter]
                  : [...BATCH_STATUS_OPTIONS.citi, ...BATCH_STATUS_OPTIONS.third_party.filter((o) => !BATCH_STATUS_OPTIONS.citi.some((c) => c.value === o.value))]
                ).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select
                value={batchTypeFilter}
                onChange={(e) => {
                  const newType = e.target.value;
                  setBatchTypeFilter(newType);
                  if (newType === 'citi' || newType === 'third_party') {
                    const validValues = BATCH_STATUS_OPTIONS[newType].map((o) => o.value);
                    if (batchStatusFilter && !validValues.includes(batchStatusFilter)) setBatchStatusFilter('');
                  }
                }}
                className="h-9 px-2.5 rounded-md border border-border/60 bg-white text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <option value="">All types</option>
                <option value="citi">Citi</option>
                <option value="third_party">Third party</option>
              </select>
              <select
                value={batchCurrencyFilter}
                onChange={(e) => setBatchCurrencyFilter(e.target.value)}
                className="h-9 px-2.5 rounded-md border border-border/60 bg-white text-sm text-muted-foreground focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <option value="">All currencies</option>
                {batchCurrencies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button
                onClick={() => { setExpandedBatches(new Set()); loadBatches(true, batchStatusFilter, batchTypeFilter, batchCurrencyFilter); }}
                disabled={loadingBatches}
                className="h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              >
                {loadingBatches ? 'Loading…' : 'Refresh'}
              </button>
            </div>
            {loadingBatches ? (
              <p className="text-sm text-muted-foreground">Loading batches…</p>
            ) : batches.length === 0 ? (
              <div className="bg-white border border-border/60 rounded-xl p-8 text-center">
                <p className="text-sm text-muted-foreground">No payment batches yet.</p>
              </div>
            ) : (
              <>{batches.map((batch) => {
                const isPayroll = batch.items.length > 0 && batch.items.every((i) => i.paymentType === 'CITI153');
                const itemLabel = isPayroll ? 'employee' : 'invoice';
                const isExpanded = expandedBatches.has(batch.id);
                return (
                <div key={batch.id} className="bg-[#fafafa] border border-border/60 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
                    <button
                      onClick={() => toggleBatchExpanded(batch.id)}
                      className="flex items-start gap-2 text-left flex-1 min-w-0"
                    >
                      <span className="mt-0.5 shrink-0 text-muted-foreground">
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </span>
                      <div>
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-sm font-semibold">{batch.batchReference}</p>
                          <p className="text-xs text-muted-foreground">
                            {batch.itemCount} {itemLabel}{batch.itemCount !== 1 ? 's' : ''} · {Object.entries(batch.items.reduce((acc, item) => { acc[item.currency] = (acc[item.currency] ?? 0) + item.amount; return acc; }, {} as Record<string, number>)).map(([ccy, amt]) => `${amt.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} ${ccy}`).join(' · ')}
                          </p>
                          <p className="text-xs text-muted-foreground ml-4">
                            Created {formatDateTime(batch.createdAt)}
                            {batch.confirmedAt && ` · Sent ${formatDateTime(batch.confirmedAt)}`}
                            {batch.executionDate && batch.batchType !== 'third_party' && ` · Execution ${formatDate(batch.executionDate)}`}
                          </p>
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      {(() => {
                        const s = STATUS_STYLES[batch.status] ?? { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', label: batch.status };
                        return (
                          <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${s.bg} ${s.text} ${s.border}`}>
                            {s.label}
                          </span>
                        );
                      })()}
                      <button
                        onClick={() => setViewingTimelineBatch(batch.id)}
                        title="View timeline"
                        className="flex items-center justify-center h-7 w-7 rounded-md border border-border/60 bg-white text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <History size={13} />
                      </button>
                      <button
                        onClick={() => handleDownloadBatch(batch.id)}
                        disabled={downloadingBatch === batch.id}
                        title={batch.batchType === 'third_party' ? 'Download XLSX' : 'Download XML'}
                        className="flex items-center justify-center h-7 w-7 rounded-md border border-border/60 bg-white text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                      >
                        <Download size={13} />
                      </button>
                      {batch.status === 'pending' && batch.batchType !== 'third_party' && (
                        <button
                          onClick={() => setPendingSendBatch(batch.id)}
                          disabled={confirmingBatch === batch.id}
                          className="text-xs h-8 px-3 rounded-md border border-border/60 bg-white text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                        >
                          {confirmingBatch === batch.id ? 'Sending…' : 'Send to bank'}
                        </button>
                      )}
                      {(batch.status === 'pending' || (batch.status === 'being_paid' && batch.batchType === 'third_party')) && (
                        <button
                          onClick={() => setDeletingBatch(batch.id)}
                          className="text-xs h-8 px-3 rounded-md border border-destructive/30 bg-white text-destructive/70 hover:text-destructive transition-colors"
                        >
                          Delete
                        </button>
                      )}
                      {['being_paid', 'bank_failed', 'fortnox_failed'].includes(batch.status) && batch.batchType !== 'third_party' && (
                        <button
                          onClick={() => setPendingCancelBatch(batch.id)}
                          disabled={cancellingBatch === batch.id}
                          title="Force-cancel locally, regardless of what Citi shows — use when the bank silently rejected the file"
                          className="text-xs h-8 px-3 rounded-md border border-destructive/30 bg-white text-destructive/70 hover:text-destructive transition-colors disabled:opacity-40"
                        >
                          {cancellingBatch === batch.id ? 'Cancelling…' : 'Cancel batch'}
                        </button>
                      )}
                      {['being_paid', 'partial', 'bank_failed', 'fortnox_failed'].includes(batch.status) && (batch.batchType === 'third_party' || isPayroll) && (
                        <button
                          onClick={() => setPendingMarkPaidBatch(batch.id)}
                          disabled={markingPaidBatch === batch.id}
                          title="Mark this batch as paid without going through Fortnox — use when the payment was handled manually"
                          className="text-xs h-8 px-3 rounded-md border border-border/60 bg-white text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                        >
                          {markingPaidBatch === batch.id ? 'Marking…' : 'Mark as paid'}
                        </button>
                      )}
                      {user?.isAdmin && batch.items.some((i) => i.status === 'bank_approved') && (
                        <button
                          onClick={() => setPendingForceFortnoxBatch(batch.id)}
                          disabled={forcingFortnoxBatch === batch.id}
                          title="Create the Fortnox payment directly, bypassing the bank confirmation — use only while Citi is not sending EndToEndId on transactions"
                          className="text-xs h-8 px-3 rounded-md border border-destructive/30 bg-white text-destructive/70 hover:text-destructive transition-colors disabled:opacity-40"
                        >
                          {forcingFortnoxBatch === batch.id ? 'Queueing…' : 'Force Fortnox pay'}
                        </button>
                      )}
                      {batch.items.some((i) => i.status === 'bank_failed') && (
                        <button
                          onClick={() => handleResubmitFailed(batch.id)}
                          disabled={resubmittingBatch === batch.id}
                          className="text-xs h-8 px-3 rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                        >
                          {resubmittingBatch === batch.id ? 'Creating…' : 'Resubmit failed'}
                        </button>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                  <>
                  {batch.errorMessage && (
                    <div className="flex items-start gap-2 px-5 py-3 bg-destructive/5 border-b border-destructive/15 text-xs text-destructive">
                      <span className="mt-0.5 shrink-0">⚠</span>
                      <span className="break-all">{batch.errorMessage}</span>
                    </div>
                  )}
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border/40 bg-muted/60">
                        <th className="px-5 py-2.5 text-left font-medium text-muted-foreground">{isPayroll ? 'Employee' : 'Supplier'}</th>
                        {!isPayroll && <th className="px-5 py-2.5 text-left font-medium text-muted-foreground">Doc #</th>}
                        <th className="px-5 py-2.5 text-right font-medium text-muted-foreground">Amount</th>
                        <th className="px-5 py-2.5 text-left font-medium text-muted-foreground">Method</th>
                        <th className="px-5 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batch.items.map((item, idx) => {
                        const s = STATUS_STYLES[item.status] ?? { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', label: item.status };
                        return (
                          <tr key={item.documentNumber ?? idx} className="border-b border-border/40 last:border-0">
                            <td className="px-5 py-2.5">{item.supplierName}</td>
                            {!isPayroll && <td className="px-5 py-2.5 text-muted-foreground font-mono">{item.documentNumber}</td>}
                            <td className="px-5 py-2.5 text-right font-mono">
                              {item.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {item.currency}
                            </td>
                            <td className="px-5 py-2.5 text-muted-foreground">{item.paymentType}</td>
                            <td className="px-5 py-2.5">
                              <span
                                className={`inline-flex items-center gap-1 ${s.bg} ${s.text} border ${s.border} px-2 py-0.5 rounded-full`}
                                title={item.errorMessage ?? undefined}
                              >
                                {s.label}
                                {item.errorMessage && <span className="opacity-70">ⓘ</span>}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </>
                  )}
                </div>
                );
              })}
              {batches.length < batchTotal && (
                <button
                  onClick={loadMoreBatches}
                  disabled={loadingMoreBatches}
                  className="w-full h-9 rounded-md border border-border/60 bg-white text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                >
                  {loadingMoreBatches ? 'Loading…' : `Load more (${batchTotal - batches.length} remaining)`}
                </button>
              )}
              </>
            )}
          </div>
        )}
      </div>

      {deletingBatch !== null && (
        <ConfirmModal
          message="Delete this batch? The invoices will return to the list."
          onConfirm={() => handleDeleteBatch(deletingBatch)}
          onCancel={() => setDeletingBatch(null)}
        />
      )}

      {pendingSendBatch !== null && (
        <ConfirmModal
          message="Send this batch to the bank? This uploads the payment file to Citi."
          confirmLabel="Send to bank"
          destructive={false}
          onConfirm={() => { const id = pendingSendBatch; setPendingSendBatch(null); handleConfirm(id); }}
          onCancel={() => setPendingSendBatch(null)}
        />
      )}

      {pendingCancelBatch !== null && (
        <ConfirmModal
          message="Cancel this batch? This marks it as cancelled on our side regardless of what Citi shows — only use this when you manually rejected the file on the Citi portal. Invoices will return to the list."
          confirmLabel="Cancel batch"
          onConfirm={() => handleCancelBatch(pendingCancelBatch)}
          onCancel={() => setPendingCancelBatch(null)}
        />
      )}

      {pendingMarkPaidBatch !== null && (
        <ConfirmModal
          message="Are you sure? The system will assume this was handled manually and all the pending payments will be marked as paid."
          confirmLabel="Mark as paid"
          destructive={false}
          onConfirm={() => handleMarkPaid(pendingMarkPaidBatch)}
          onCancel={() => setPendingMarkPaidBatch(null)}
        />
      )}

      {pendingForceFortnoxBatch !== null && (
        <ConfirmModal
          message="This queues the Fortnox payment creation (processed within a few minutes, after any running Fortnox jobs), bypassing the bank confirmation (camt.053) step entirely. Only use this for items stuck at Bank Approved because Citi failed to send the EndToEndId reference. This cannot be undone."
          confirmLabel="Force Fortnox pay"
          onConfirm={() => handleForceFortnoxPay(pendingForceFortnoxBatch)}
          onCancel={() => setPendingForceFortnoxBatch(null)}
        />
      )}

      {viewingTimelineBatch !== null && (
        <BatchTimelineModal
          events={timelineEvents}
          loading={loadingTimeline}
          onClose={() => { setViewingTimelineBatch(null); setTimelineEvents([]); }}
        />
      )}
    </Layout>
  );
}
